import {
	Component,
	inject,
	OnInit,
	TemplateRef,
	ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
	NgbActiveModal,
	NgbDropdownModule,
	NgbNavModule,
	NgbModal,
} from '@ng-bootstrap/ng-bootstrap';
import {
	GameCustomizationService,
	PluginType,
	StylePreset,
	UIAnimations,
} from '@meta-press/shared-plugins';
import { SettingsService } from '../../services/settings-service/settings.service';

/**
 * Component for managing all saved style presets
 * Allows viewing, editing, and deleting presets organized by plugin type
 */
@Component({
	selector: 'app-preset-management',
	standalone: true,
	imports: [CommonModule, FormsModule, NgbDropdownModule, NgbNavModule],
	templateUrl: './preset-management.component.html',
	styleUrl: './preset-management.component.scss',
	animations: UIAnimations,
})
export class PresetManagementComponent implements OnInit {
	// Inject services
	private gameCustomizationService = inject(GameCustomizationService);
	private modalService = inject(NgbModal);
	private settingsService = inject(SettingsService);
	public activeModal = inject(NgbActiveModal);

	// All presets
	allPresets: StylePreset[] = [];

	// Filter and sort state
	filterPluginType: PluginType | null = null;
	filterText: string = '';
	sortColumn: 'name' | 'date' | 'plugin' | null = null;
	sortDirection: 'asc' | 'desc' = 'asc';

	// Available plugin types for dropdown
	availablePluginTypes: Array<{
		type: PluginType;
		title: string;
		description: string;
	}> = [];

	// Reference to delete confirmation modal template
	@ViewChild('deletePresetModal') deletePresetModalRef!: TemplateRef<any>;

	// Preset pending deletion (for confirmation modal)
	presetToDelete: StylePreset | null = null;

	ngOnInit(): void {
		this.loadPresets();
		this.loadAvailablePluginTypes();
	}

	/**
	 * Load available plugin types filtered by environment settings
	 */
	private loadAvailablePluginTypes(): void {
		this.availablePluginTypes =
			this.settingsService.getFilteredPluginTypes();
	}

	/**
	 * Load all presets
	 */
	private loadPresets(): void {
		this.allPresets = this.gameCustomizationService.getSavedStylePresets();
	}

	/**
	 * Refresh the preset list after changes
	 */
	refreshPresets(): void {
		this.loadPresets();
	}

	/**
	 * Get filtered and sorted presets for display
	 */
	getFilteredPresets(): StylePreset[] {
		let filtered = [...this.allPresets];

		// Filter by plugin type
		if (this.filterPluginType !== null) {
			filtered = filtered.filter(
				(preset) => preset.createdFromPlugin === this.filterPluginType,
			);
		}

		// Filter by text (name or description)
		if (this.filterText.trim()) {
			const searchText = this.filterText.toLowerCase();
			filtered = filtered.filter(
				(preset) =>
					preset.name.toLowerCase().includes(searchText) ||
					preset.description?.toLowerCase().includes(searchText),
			);
		}

		// Apply sorting
		return this.applySorting(filtered);
	}

	/**
	 * Apply sorting to preset list based on current sort column and direction
	 * @private
	 */
	private applySorting(presets: StylePreset[]): StylePreset[] {
		if (!this.sortColumn) {
			return presets;
		}

		return [...presets].sort((a, b) => {
			let aValue: string | number;
			let bValue: string | number;

			switch (this.sortColumn) {
				case 'name':
					aValue = a.name.toLowerCase();
					bValue = b.name.toLowerCase();
					break;
				case 'date':
					aValue = a.modifiedAt;
					bValue = b.modifiedAt;
					break;
				case 'plugin':
					aValue = a.createdFromPlugin;
					bValue = b.createdFromPlugin;
					break;
				default:
					return 0;
			}

			let comparison = 0;
			if (aValue < bValue) {
				comparison = -1;
			} else if (aValue > bValue) {
				comparison = 1;
			}

			return this.sortDirection === 'asc' ? comparison : -comparison;
		});
	}

	/**
	 * Sort presets by clicking on table header
	 */
	sortPresets(column: 'name' | 'date' | 'plugin'): void {
		if (this.sortColumn === column) {
			// Toggle direction if clicking same column
			this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
		} else {
			// Set new column and reset to ascending
			this.sortColumn = column;
			this.sortDirection = 'asc';
		}
	}

	/**
	 * Get Bootstrap icon class for sort indicator
	 */
	getSortIcon(column: 'name' | 'date' | 'plugin'): string {
		if (this.sortColumn !== column) {
			return 'bi-arrow-down-up'; // Default sort icon
		}
		return this.sortDirection === 'asc'
			? 'bi-sort-alpha-down'
			: 'bi-sort-alpha-up';
	}

	/**
	 * Get display name for a plugin type
	 */
	getPluginTypeName(pluginType: PluginType): string {
		const pt = this.availablePluginTypes.find((p) => p.type === pluginType);
		return pt?.title || 'Tuntematon';
	}

	/**
	 * Format date for display
	 */
	formatDate(timestamp: number): string {
		const date = new Date(timestamp);
		return date.toLocaleDateString('fi-FI', {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
		});
	}

	/**
	 * Open preset for editing (placeholder - shows "not implemented" message)
	 */
	editPreset(preset: StylePreset): void {
		alert('Esiasetuksen muokkaus ei ole vielä toteutettu');
	}

	/**
	 * Confirm preset deletion
	 */
	confirmDeletePreset(
		preset: StylePreset,
		modalTemplate: TemplateRef<any>,
	): void {
		this.presetToDelete = preset;
		this.modalService.open(modalTemplate, {
			centered: true,
			size: 'sm',
		});
	}

	/**
	 * Delete the confirmed preset
	 */
	deletePreset(): void {
		if (this.presetToDelete) {
			this.gameCustomizationService.deleteStylePreset(
				this.presetToDelete.id,
			);
			this.refreshPresets();
			this.presetToDelete = null;
		}
	}

	/**
	 * Cancel preset deletion
	 */
	cancelDelete(): void {
		this.presetToDelete = null;
	}

	/**
	 * TrackBy function for preset list
	 */
	trackByPresetId(index: number, preset: StylePreset): string {
		return preset.id;
	}

	/**
	 * Clear all filters
	 */
	clearFilters(): void {
		this.filterPluginType = null;
		this.filterText = '';
	}

	/**
	 * Check if any filters are active
	 */
	hasActiveFilters(): boolean {
		return this.filterPluginType !== null || this.filterText.trim() !== '';
	}

	/**
	 * Close the modal
	 */
	close(): void {
		this.activeModal.dismiss();
	}
}
